// Proyecto Empresarial — pantalla de creación.
//
// QUÉ ES: el plan de negocios COMERCIAL del cliente (estudio de mercado, plan
// operativo, técnico, financiero e implementación). NO es el proyecto de la
// visa; ese es "Propuestas EB-2 NIW" y vive en CreateNIWInteractive.
//
// El texto del proyecto puede venir de tres lugares y el orden importa: lo que
// se escriba acá gana, después el archivo que se suba, y si no hay nada de eso
// el backend lo toma del dossier del cliente. Ese último caso es el normal:
// redactora ya tiene el proyecto cargado y no hay que volver a adjuntarlo.
//
// LA UBICACIÓN SE PRECARGA. /empresarial/prefill la busca en la hoja canónica
// del cliente, en su ficha y en el CV del dossier, en ese orden, y dice de
// dónde salió. Es una lectura barata: no arma hojas ni llama al modelo.
//
// SE CONSULTA ANTES DE ESCRIBIR. Monica propone tres ENFOQUES COMERCIALES
// del mismo negocio —a que segmento le vende, con que modelo de ingresos, por
// donde entra— y el operador elige. El elegido viaja a todas las secciones,
// asi que elegir no es decorativo. Si la propuesta falla, se puede generar
// igual con el titulo escrito a mano: nunca es un bloqueo.
//
// NO SE PREGUNTA EL IDIOMA. Se redacta en inglés —es el idioma del
// entregable— y el backend traduce al español al terminar, con el modelo
// barato. La ficha del documento muestra las dos versiones.
//
// LA PANTALLA NO SE QUEDA ESPERANDO. La generación son ocho minutos y corre en
// el servidor; a los pocos segundos esta pantalla vuelve sola a la ficha del
// cliente para poder seguir generando otros documentos. El que quiera ver el
// avance lo dice y se queda.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Loader2, Upload, FileText, AlertTriangle, Play,
  Lightbulb, Check, Star,
} from 'lucide-react';
import { API } from '../utils/constants';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Cuánto se muestra el avance antes de volver a la ficha del cliente. Ocho
// segundos alcanzan para leer que arrancó y ver la primera sección en cola,
// y no obligan a nadie a mirar ocho minutos de barra de progreso.
const SEGUNDOS_PARA_VOLVER = 8;

// Las siete secciones, para poder mostrar el avance con nombre y no solo un
// porcentaje. El orden es el de LECTURA; el backend las genera por dependencia.
const SECCIONES = [
  'Resumen Ejecutivo',
  'Estudio de Mercado',
  'Plan Operativo',
  'Plan Técnico',
  'Plan Financiero',
  'Plan de Implementación',
  'Fuentes y Supuestos',
];

export default function CreateEmpresarial() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const clientId = params.get('client_id') || '';

  const [cliente, setCliente] = useState(null);
  const [negocio, setNegocio] = useState('');
  const [titulo, setTitulo] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [texto, setTexto] = useState('');
  const [archivo, setArchivo] = useState(null);

  const [precarga, setPrecarga] = useState(null);

  const [opciones, setOpciones] = useState(null);   // la respuesta de Mónica
  const [pidiendo, setPidiendo] = useState(false);

  const [fase, setFase] = useState('form');      // form | opciones | generando
  const [restante, setRestante] = useState(SEGUNDOS_PARA_VOLVER);
  const [quedarse, setQuedarse] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [proyectoId, setProyectoId] = useState(null);
  const [estado, setEstado] = useState(null);
  const timer = useRef(null);

  // Precarga. Solo llena los campos que están vacíos: si el operador ya
  // escribió algo, lo que escribió gana.
  useEffect(() => {
    if (!clientId) return;
    axios.get(`${API}/empresarial/prefill`, { params: { client_id: clientId }, headers: auth() })
      .then((r) => {
        const p = r.data || {};
        setPrecarga(p);
        setCliente({ name: p.client_name || '' });
        setTitulo((t) => t || p.project_title || '');
        setUbicacion((u) => u || p.location || '');
        setNegocio((n) => n || p.business_name || '');
      })
      .catch(() => {
        // La precarga es una comodidad, no un requisito: si falla, el
        // formulario se tiene que poder usar igual. Se cae al nombre del
        // cliente, que es lo único que la pantalla muestra.
        axios.get(`${API}/clients/${clientId}`, { headers: auth() })
          .then((r) => {
            setCliente(r.data);
            setTitulo((t) => t || `Proyecto Empresarial — ${r.data?.name || ''}`.trim());
          })
          .catch(() => {});
      });
  }, [clientId]);

  const consultar = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/empresarial/${id}/status`, { headers: auth() });
      setEstado(r.data);
      if (r.data.status === 'completed') {
        clearInterval(timer.current);
        toast.success('Proyecto Empresarial listo');
        navigate(`/view-empresarial/${id}`);
      } else if (r.data.status === 'error') {
        clearInterval(timer.current);
        toast.error(r.data.error_message || 'La generación falló');
      }
    } catch (e) {
      // Un error de red suelto no puede matar el sondeo: la generación sigue
      // corriendo en el backend y la próxima vuelta probablemente conteste.
    }
  }, [navigate]);

  useEffect(() => {
    if (fase !== 'generando' || !proyectoId) return undefined;
    consultar(proyectoId);
    timer.current = setInterval(() => consultar(proyectoId), 6000);
    return () => clearInterval(timer.current);
  }, [fase, proyectoId, consultar]);

  const volverAlCliente = useCallback(() => {
    navigate(clientId ? `/client-dashboard/${clientId}` : '/dashboard');
  }, [navigate, clientId]);

  // La vuelta automática. La generación no vive en esta pantalla: la dispara
  // el backend y sigue sola, así que no hay nada que se pierda al salir.
  useEffect(() => {
    if (fase !== 'generando' || quedarse) return undefined;
    if (estado?.status === 'error') return undefined;   // que se lea el error
    if (restante <= 0) {
      volverAlCliente();
      return undefined;
    }
    const t = setTimeout(() => setRestante((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [fase, quedarse, restante, estado, volverAlCliente]);

  // `titulo` y `enfoque` se pasan explícitos: cuando se elige una opción, el
  // estado de React todavía no está actualizado en la misma vuelta y el
  // proyecto arrancaría con el título anterior.
  const arrancar = async (tituloElegido, enfoque = '') => {
    if (!clientId) {
      toast.error('Abrí esta pantalla desde la ficha de un cliente.');
      return;
    }
    const tituloFinal = tituloElegido || titulo;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('client_id', clientId);
      if (negocio) fd.append('business_name', negocio);
      if (tituloFinal) fd.append('project_title', tituloFinal);
      if (enfoque) fd.append('project_focus', enfoque);
      if (ubicacion) fd.append('location', ubicacion);
      if (texto.trim()) fd.append('project_description', texto.trim());
      if (archivo) fd.append('file', archivo);
      fd.append('generate_now', 'true');

      const r = await axios.post(`${API}/empresarial/start`, fd, { headers: auth() });
      setProyectoId(r.data.id);
      setFase('generando');
      toast.success('Generación iniciada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No pude iniciar el proyecto');
    } finally {
      setEnviando(false);
    }
  };

  const pedirOpciones = async () => {
    if (!clientId) {
      toast.error('Abrí esta pantalla desde la ficha de un cliente.');
      return;
    }
    setPidiendo(true);
    try {
      const r = await axios.post(`${API}/empresarial/suggest-options`, {
        client_id: clientId,
        business_name: negocio || null,
        project_title: titulo || null,
        location: ubicacion || null,
        project_description: texto.trim() || null,
      }, { headers: auth() });
      setOpciones(r.data);
      setFase('opciones');
    } catch (e) {
      // La propuesta no es un requisito: el mensaje del backend ya dice que se
      // puede generar con el título escrito a mano, y ese botón sigue ahí.
      toast.error(e?.response?.data?.detail || 'No pude proponer opciones');
    } finally {
      setPidiendo(false);
    }
  };

  const hechas = estado?.sections_done || 0;
  const pct = estado?.progress || 0;

  // La precarga DICE DE DÓNDE SALIÓ. Una ciudad que aparece sola en el campo
  // se da por buena sin mirarla; una que explica su origen se revisa cuando
  // hace falta — y el backend avisa cuando la ubicación es la residencia
  // actual del cliente y no necesariamente donde va a operar.
  const BASE_UBICACION = 'Donde va a operar. Entra en el plan operativo y en el técnico.';
  const precargada = precarga?.location && ubicacion === precarga.location;
  const ubicacionHint = precargada
    ? `${BASE_UBICACION} ${precarga.location_note || ''}`.trim()
    : BASE_UBICACION;
  const negocioHint = (precarga?.business_name && negocio === precarga.business_name)
    ? 'Precargado del campo "empresa" de la ficha del cliente. Cambialo si la razón social es otra.'
    : 'Como se llama la empresa. Si todavía no tiene nombre, dejalo vacío.';

  if (fase === 'opciones' && opciones) {
    const recomendada = opciones.recommended_index ?? 0;
    return (
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
        <button
          onClick={() => setFase('form')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 0, color: '#6B7280', fontSize: '0.85rem', cursor: 'pointer', padding: 0, marginBottom: '1.25rem' }}
        >
          <ArrowLeft size={15} /> Volver al formulario
        </button>

        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#111827', margin: 0 }}>
          Elegí el enfoque del plan
        </h1>
        <p style={{ color: '#6B7280', fontSize: '0.9rem', marginTop: '0.35rem', marginBottom: '0.5rem' }}>
          Son tres formas de llevar <strong>el mismo negocio</strong> al mercado.
          El que elijas se usa para escribir las siete secciones, así que el
          documento sale coherente con esa decisión.
        </p>
        <p style={{ color: '#6B7280', fontSize: '0.83rem', marginBottom: '1.5rem' }}>
          {opciones.domain && <>Sector detectado: <strong>{opciones.domain}</strong>. </>}
          Analizado sobre {opciones.source === 'dossier'
            ? 'el proyecto cargado en el dossier del cliente'
            : 'la descripción que escribiste'}
          {opciones.project_chars ? ` (${opciones.project_chars.toLocaleString('es')} caracteres)` : ''}.
        </p>

        <div style={{ display: 'grid', gap: '0.9rem' }}>
          {(opciones.options || []).map((o, i) => (
            <div
              key={i}
              style={{
                background: '#FFFFFF',
                border: `1px solid ${i === recomendada ? '#0F766E' : '#E5E7EB'}`,
                borderRadius: 12, padding: '1.1rem 1.2rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                <div style={{ flex: 1 }}>
                  {i === recomendada && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#ECFDF5', color: '#0F766E', borderRadius: 999, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                      <Star size={11} /> Recomendada
                    </div>
                  )}
                  <div style={{ fontSize: '1.02rem', fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
                    {o.title}
                  </div>
                  {o.focus && (
                    <div style={{ color: '#0F766E', fontSize: '0.86rem', marginTop: '0.3rem' }}>
                      {o.focus}
                    </div>
                  )}
                  {o.description && (
                    <p style={{ color: '#4B5563', fontSize: '0.87rem', margin: '0.5rem 0 0', lineHeight: 1.55 }}>
                      {o.description}
                    </p>
                  )}
                  <div style={{ display: 'grid', gap: '0.25rem', marginTop: '0.6rem' }}>
                    {o.strength && (
                      <div style={{ fontSize: '0.82rem', color: '#374151' }}>
                        <strong>A favor:</strong> {o.strength}
                      </div>
                    )}
                    {o.risk && (
                      <div style={{ fontSize: '0.82rem', color: '#374151' }}>
                        <strong>Riesgo:</strong> {o.risk}
                      </div>
                    )}
                    {/* Lo que HABRÍA que confirmar: es la diferencia entre un
                        supuesto declarado y una cifra inventada, que es la
                        regla del módulo entero. */}
                    {o.assumption && (
                      <div style={{ fontSize: '0.82rem', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '0.4rem 0.6rem', marginTop: '0.2rem' }}>
                        <strong>Habría que confirmar:</strong> {o.assumption}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setTitulo(o.title);
                    arrancar(o.title, [o.focus, o.description].filter(Boolean).join(' '));
                  }}
                  disabled={enviando}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    background: i === recomendada ? '#0F766E' : '#111827',
                    color: '#fff', border: 0, borderRadius: 9,
                    padding: '0.6rem 0.9rem', fontSize: '0.85rem', fontWeight: 700,
                    cursor: enviando ? 'default' : 'pointer', opacity: enviando ? 0.5 : 1,
                    flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  {enviando
                    ? <Loader2 size={15} className="animate-spin" />
                    : <Check size={15} />}
                  Elegir y generar
                </button>
              </div>
            </div>
          ))}
        </div>

        {opciones.reason && (
          <div style={{ marginTop: '1.1rem', background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10, padding: '0.85rem 1rem', fontSize: '0.86rem', color: '#374151' }}>
            <strong>Por qué la recomendada:</strong> {opciones.reason}
          </div>
        )}

        <button
          onClick={() => arrancar(titulo, '')}
          disabled={enviando}
          style={{ marginTop: '1.25rem', background: 'none', border: 0, color: '#6B7280', fontSize: '0.85rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          Ninguna: generar con el título que escribí
        </button>
      </div>
    );
  }

  if (fase === 'generando') {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#111827', marginBottom: '0.35rem' }}>
          Armando el Proyecto Empresarial
        </h1>
        <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
          Son siete secciones y toma unos ocho minutos. La generación corre en
          el servidor: podés irte de esta pantalla y el documento igual queda
          en la ficha del cliente.
        </p>

        {estado?.status !== 'error' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: '1.25rem' }}>
            <span style={{ color: '#065F46', fontSize: '0.85rem' }}>
              {quedarse
                ? 'Te quedás acá viendo el avance. Al terminar se abre el documento.'
                : `Te llevamos a la ficha del cliente en ${restante} s para que sigas generando documentos.`}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={volverAlCliente}
                style={{ background: '#0F766E', color: '#fff', border: 0, borderRadius: 8, padding: '0.4rem 0.8rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Ir ahora
              </button>
              {!quedarse && (
                <button
                  onClick={() => setQuedarse(true)}
                  style={{ background: 'transparent', color: '#065F46', border: '1px solid #A7F3D0', borderRadius: 8, padding: '0.4rem 0.8rem', fontSize: '0.82rem', cursor: 'pointer' }}
                >
                  Quedarme viendo
                </button>
              )}
            </div>
          </div>
        )}

        <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.9rem' }}>
            <Loader2 size={18} className="animate-spin" style={{ color: '#0F766E' }} />
            <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>
              {estado?.progress_message || 'Preparando…'}
            </span>
            <span style={{ marginLeft: 'auto', color: '#6B7280', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
              {hechas}/7 secciones
            </span>
          </div>

          <div style={{ height: 6, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: '#0F766E', transition: 'width .4s ease' }} />
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: '1.1rem 0 0' }}>
            {SECCIONES.map((s, i) => (
              <li key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.85rem', color: i < hechas ? '#0F766E' : '#9CA3AF' }}>
                <span style={{ width: 18, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{i + 1}.</span>
                {s}
                {i < hechas && <span style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>listo</span>}
              </li>
            ))}
          </ul>
        </div>

        {estado?.status === 'error' && (
          <div style={{ marginTop: '1rem', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <AlertTriangle size={16} style={{ color: '#B91C1C', marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, color: '#7F1D1D', fontSize: '0.9rem' }}>La generación falló</div>
                <div style={{ color: '#991B1B', fontSize: '0.85rem', marginTop: 2 }}>{estado.error_message}</div>
                <div style={{ color: '#991B1B', fontSize: '0.8rem', marginTop: 6 }}>
                  Lo que ya se escribió quedó guardado. Al reintentar se generan
                  solo las secciones que faltan.
                </div>
                <button
                  onClick={() => navigate(`/view-empresarial/${proyectoId}`)}
                  style={{ marginTop: 10, background: '#111827', color: '#fff', border: 0, borderRadius: 8, padding: '0.5rem 0.9rem', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Ver lo que hay
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
      <button
        onClick={() => navigate(clientId ? `/client-dashboard/${clientId}` : '/dashboard')}
        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 0, color: '#6B7280', fontSize: '0.85rem', cursor: 'pointer', padding: 0, marginBottom: '1.25rem' }}
      >
        <ArrowLeft size={15} /> Volver
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.4rem' }}>
        <div style={{ width: 38, height: 38, background: '#ECFDF5', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Building2 size={19} style={{ color: '#0F766E' }} />
        </div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.5rem', fontWeight: 800, color: '#111827', margin: 0 }}>
          Proyecto Empresarial
        </h1>
      </div>
      <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
        El plan de negocios del cliente: estudio de mercado, plan operativo,
        técnico, financiero y de implementación. Unas 24 páginas.
      </p>
      <p style={{ color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
        No es el proyecto de la visa. Este documento cuenta el negocio como
        negocio, sin argumento migratorio, y es el que fija las cifras que
        después cita el resto del paquete.
      </p>
      {/* No se pregunta el idioma: el entregable va en inglés y el español es
          una traducción automática. Preguntarlo dejaba paquetes con el plan de
          negocios en español y el resto en inglés. */}
      <p style={{ color: '#1E3A8A', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem', marginBottom: '1.75rem' }}>
        Se redacta en inglés y al terminar se traduce solo al español. Los dos
        idiomas quedan en la ficha, con descarga en PDF y en Word.
      </p>

      {cliente && (
        <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#374151' }}>
          Cliente: <strong>{cliente.name}</strong>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1.1rem' }}>
        <Campo label="Nombre del negocio" hint={negocioHint}>
          <input value={negocio} onChange={(e) => setNegocio(e.target.value)} style={estiloInput} placeholder="Andina Safety Analytics LLC" />
        </Campo>

        <Campo label="Título del documento">
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} style={estiloInput} />
        </Campo>

        <Campo label="Ubicación" hint={ubicacionHint}>
          <input value={ubicacion} onChange={(e) => setUbicacion(e.target.value)} style={estiloInput} placeholder="Alpharetta, Georgia" />
        </Campo>


        <Campo
          label="Descripción del proyecto"
          hint="Opcional. Si lo dejás vacío se usa el proyecto cargado en el dossier del cliente, que es lo normal."
        >
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={7}
            style={{ ...estiloInput, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Pegá acá la descripción del negocio solo si querés usar un texto distinto al del dossier."
          />
        </Campo>

        <Campo label="O subir un archivo" hint="Word, PDF o texto. Lo que se suba se guarda en el dossier y lo reusan los demás documentos.">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', border: '1px dashed #CBD5E1', borderRadius: 8, padding: '0.7rem 0.9rem', cursor: 'pointer', color: '#475569', fontSize: '0.85rem' }}>
            <Upload size={16} />
            {archivo ? archivo.name : 'Elegir archivo'}
            <input
              type="file"
              accept=".pdf,.txt,.md,.doc,.docx"
              onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
            />
          </label>
        </Campo>
      </div>

      <button
        onClick={pedirOpciones}
        disabled={pidiendo || enviando || !clientId}
        style={{
          marginTop: '1.75rem', width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '0.5rem', background: '#0F766E', color: '#fff',
          border: 0, borderRadius: 10, padding: '0.85rem', fontSize: '0.95rem',
          fontWeight: 700, cursor: pidiendo ? 'default' : 'pointer',
          opacity: pidiendo || enviando || !clientId ? 0.5 : 1,
        }}
      >
        {pidiendo
          ? (<><Loader2 size={17} className="animate-spin" /> Analizando el proyecto…</>)
          : (<><Lightbulb size={17} /> Ver enfoques y elegir</>)}
      </button>

      {/* La propuesta es un paso, no una puerta: si Mónica no contesta o
          ninguna opción sirve, se genera igual con lo que hay en el
          formulario. */}
      <button
        onClick={() => arrancar(titulo, '')}
        disabled={pidiendo || enviando || !clientId}
        style={{
          marginTop: '0.7rem', width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '0.5rem', background: '#FFFFFF',
          color: '#374151', border: '1px solid #E5E7EB', borderRadius: 10,
          padding: '0.7rem', fontSize: '0.88rem', fontWeight: 600,
          cursor: enviando ? 'default' : 'pointer',
          opacity: pidiendo || enviando || !clientId ? 0.5 : 1,
        }}
      >
        {enviando
          ? (<><Loader2 size={16} className="animate-spin" /> Iniciando…</>)
          : (<><Play size={16} /> Generar sin ver enfoques</>)}
      </button>

      {!clientId && (
        <p style={{ marginTop: '0.8rem', color: '#B91C1C', fontSize: '0.83rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <AlertTriangle size={14} /> Abrí esta pantalla desde la ficha de un cliente.
        </p>
      )}

      <p style={{ marginTop: '1.5rem', color: '#9CA3AF', fontSize: '0.8rem', display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
        <FileText size={14} style={{ marginTop: 2, flexShrink: 0 }} />
        Las cifras del plan financiero salen de la hoja compartida del cliente,
        la misma que usan el proyecto de la visa y el estudio econométrico. Si
        esa hoja no se puede derivar, el plan sale con supuestos declarados en
        vez de números inventados, y la ficha del documento lo avisa.
      </p>
    </div>
  );
}

const estiloInput = {
  width: '100%', border: '1px solid #E5E7EB', borderRadius: 8,
  padding: '0.6rem 0.7rem', fontSize: '0.9rem', color: '#111827',
  background: '#FFFFFF', outline: 'none',
};

function Campo({ label, hint, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: '0.83rem', fontWeight: 700, color: '#374151', marginBottom: '0.3rem' }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', color: '#9CA3AF', fontSize: '0.78rem', marginTop: '0.3rem' }}>{hint}</span>}
    </label>
  );
}
